import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import contactPortalService from '../services/contactPortal';
import { useAuth } from '../contexts/AuthContext';
import ContactPortalShell from '../components/contact/ContactPortalShell';
import './ContactPortal.scss';

const ContactTaskDetail = () => {
  const { id } = useParams();
  const { user, setUser, fetchUser } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [sendingAttachment, setSendingAttachment] = useState(false);
  const messageInputRef = useRef(null);

  useEffect(() => {
    const loadTask = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await contactPortalService.getTaskById(id);
        setTask(data);
      } catch (err) {
        setError('No se pudo cargar el detalle de la tarea.');
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [id]);

  useEffect(() => {
    const loadAttachments = async () => {
      try {
        setLoadingAttachments(true);
        const data = await contactPortalService.getTaskAttachments(id);
        setAttachments(data);
      } catch (err) {
        console.error('No se pudieron cargar adjuntos:', err);
      } finally {
        setLoadingAttachments(false);
      }
    };

    if (id) {
      loadAttachments();
    }
  }, [id]);

  useEffect(() => {
    const loadChat = async () => {
      try {
        setLoadingChat(true);
        const data = await contactPortalService.getTaskChatMessages(id);
        setChatMessages(data);
      } catch (err) {
        console.error('No se pudo cargar chat:', err);
      } finally {
        setLoadingChat(false);
      }
    };

    if (id) {
      loadChat();
    }
  }, [id]);

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatFileSize = (size) => {
    if (!size || Number.isNaN(size)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = Number(size);
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }
    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  };

  const getAuthorLabel = (author) => {
    if (!author) return 'Usuario';
    if (author.first_name || author.last_name) {
      return `${author.first_name || ''} ${author.last_name || ''}`.trim();
    }
    return author.email || author.username || 'Usuario';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;

    try {
      setSendingMessage(true);
      await contactPortalService.createTaskChatMessage(id, newMessage.trim());
      setNewMessage('');
      const data = await contactPortalService.getTaskChatMessages(id);
      setChatMessages(data);
    } catch (err) {
      alert('No se pudo enviar el mensaje. Intente nuevamente.');
    } finally {
      setSendingMessage(false);
    }
  };

  const adjustMessageInputHeight = () => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const minHeight = 44;
    const maxHeight = 120;
    const nextHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    adjustMessageInputHeight();
  }, [newMessage]);

  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    if (!selectedAttachment || sendingAttachment) return;

    try {
      setSendingAttachment(true);
      await contactPortalService.uploadTaskAttachment(id, selectedAttachment);
      setSelectedAttachment(null);
      const input = document.getElementById('contact-attachment-input');
      if (input) {
        input.value = '';
      }
      const data = await contactPortalService.getTaskAttachments(id);
      setAttachments(data);
    } catch (err) {
      alert('No se pudo subir el archivo. Verifique tipo y tamaño permitido.');
    } finally {
      setSendingAttachment(false);
    }
  };

  const onLogout = async () => {
    try {
      await contactPortalService.logout();
    } finally {
      setUser(null);
      await fetchUser();
      window.location.href = '/contactos/login';
    }
  };

  return (
    <ContactPortalShell
      user={user}
      onLogout={onLogout}
      searchValue={search}
      onSearchChange={setSearch}
    >
      <div className="detail-page-wrap">
        <div className="detail-page-header">
          <Link to="/consultas-y-reclamos" className="btn btn-secondary">Volver a bandeja</Link>
          <Link to="/consultas-y-reclamos/create" className="btn btn-primary">Nueva consulta</Link>
        </div>

        {loading && <div className="portal-box">Cargando...</div>}
        {error && <div className="portal-box error-message">{error}</div>}

        {!loading && !error && task && (
          <div className="detail-page-grid">
            <section className="portal-box detail-box">
              <div className="detail-header">
                <h2>{task.title}</h2>
                <span
                  className="status-badge"
                  style={{
                    color: task.status?.color || '#1f2937',
                    backgroundColor: task.status?.background_color || '#e5e7eb'
                  }}
                >
                  {task.status?.name || 'Sin estado'}
                </span>
              </div>

              <p className="task-description">{task.description || 'Sin descripcion'}</p>

              <div className="detail-grid">
                <div>
                  <span className="label">ID</span>
                  <span className="value">{task.id}</span>
                </div>
                <div>
                  <span className="label">Prioridad</span>
                  <span className="value">{task.priority || 'P4'}</span>
                </div>
                <div>
                  <span className="label">Canal</span>
                  <span className="value">{task.canal_codigo || 'No especificado'}</span>
                </div>
                <div>
                  <span className="label">Categoria</span>
                  <span className="value">{task.category_name || 'No especificado'}</span>
                </div>
                <div>
                  <span className="label">Formulario</span>
                  <span className="value">{task.form_name || 'No especificado'}</span>
                </div>
                <div>
                  <span className="label">Creada</span>
                  <span className="value">{new Date(task.created_at).toLocaleString('es-AR')}</span>
                </div>
                <div>
                  <span className="label">Actualizada</span>
                  <span className="value">{new Date(task.updated_at).toLocaleString('es-AR')}</span>
                </div>
              </div>

              <div className="portal-chat-section">
                <h3>Chat</h3>

                <div className="portal-chat-list" role="log" aria-live="polite">
                  {loadingChat ? (
                    <div>Cargando chat...</div>
                  ) : chatMessages.length === 0 ? (
                    <div>No hay mensajes aún.</div>
                  ) : (
                    chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`portal-chat-message ${msg.is_from_contact ? 'from-contact' : 'from-staff'}`}
                      >
                        <div className="portal-chat-meta">
                          <strong>{getAuthorLabel(msg.author)}</strong>
                          <span>•</span>
                          <span>{formatDate(msg.created_at)}</span>
                        </div>
                        <div className="portal-chat-bubble">{msg.message}</div>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={handleSendMessage} className="portal-chat-composer">
                  <textarea
                    ref={messageInputRef}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      adjustMessageInputHeight();
                    }}
                    rows={1}
                    placeholder="Escriba su mensaje"
                    className="portal-chat-input"
                    disabled={sendingMessage}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!newMessage.trim() || sendingMessage}
                  >
                    {sendingMessage ? 'Enviando...' : 'Enviar'}
                  </button>
                </form>
              </div>
            </section>

            <aside className="portal-box attachments-box portal-attachments-section">
              <h3>Documentación adjunta</h3>

              <form onSubmit={handleUploadAttachment} className="portal-attachments-composer">
                <input
                  id="contact-attachment-input"
                  type="file"
                  className="portal-attachment-hidden-input"
                  onChange={(e) => setSelectedAttachment(e.target.files?.[0] || null)}
                  disabled={sendingAttachment}
                />
                <div className="portal-attachment-picker">
                  <label htmlFor="contact-attachment-input" className="portal-attachment-pick-btn">
                    Seleccionar archivo
                  </label>
                  <span className={`portal-attachment-picked ${selectedAttachment ? 'has-file' : ''}`}>
                    {selectedAttachment ? selectedAttachment.name : 'Ningún archivo seleccionado'}
                  </span>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!selectedAttachment || sendingAttachment}
                >
                  {sendingAttachment ? 'Subiendo...' : 'Subir'}
                </button>
              </form>

              <div className="portal-attachments-list">
                {loadingAttachments ? (
                  <div className="portal-attachments-empty">Cargando adjuntos...</div>
                ) : attachments.length === 0 ? (
                  <div className="portal-attachments-empty">No hay archivos adjuntos.</div>
                ) : (
                  attachments.map((item) => (
                    <a
                      key={item.id}
                      href={item.file_url || item.file}
                      target="_blank"
                      rel="noreferrer"
                      className="portal-attachment-item"
                    >
                      <strong>{item.original_name || 'Archivo'}</strong>
                      <span>{formatFileSize(item.size)} · {formatDate(item.created_at)}</span>
                    </a>
                  ))
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
    </ContactPortalShell>
  );
};

export default ContactTaskDetail;
